import { createEffect, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Archive, ArchiveRestore, Trash2 } from "@fleet/icons";

import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Select } from "@/components/ui/select";
import { Label, TextField } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import {
  cn,
  type GoalKind,
  type HabitRow,
  normalizeUnit,
  parseWeekdays,
  type ReminderRow,
  type ScheduleKind,
  serializeWeekdays,
} from "@habits/shared";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useHabitMutations } from "../hooks/use-habit-mutations";
import { DEFAULT_HABIT_COLOR, DEFAULT_HABIT_ICON, HABIT_COLORS, HABIT_ICONS } from "../constants";

interface HabitFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Habit to edit (undefined = create). */
  habit?: HabitRow | null;
  /** Existing reminders for habit (edit only). */
  reminders?: ReminderRow[];
  /** sort_order for new habits (at the end). */
  nextSortOrder?: number;
  /**
   * URL-addressable delete confirmation (#15b): when parent
   * manages it (`?edit=<id>&confirm=delete`), passes this controlled pair. If omitted,
   * dialog uses local signal (compat / isolated tests).
   */
  confirmDeleteOpen?: boolean;
  onConfirmDeleteOpenChange?: (open: boolean) => void;
}

const WEEKDAY_KEYS = [1, 2, 3, 4, 5, 6, 7] as const;
const DEFAULT_TIMES_PER_WEEK = 3;
const MAX_TIMES_PER_WEEK = 7;
const DEFAULT_TARGET_AMOUNT = 8;
const MAX_TARGET_AMOUNT = 100_000;

export function HabitFormDialog(props: HabitFormDialogProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const mutations = useHabitMutations();

  const [name, setName] = createSignal("");
  const [icon, setIcon] = createSignal<string>(DEFAULT_HABIT_ICON);
  const [color, setColor] = createSignal<string>(DEFAULT_HABIT_COLOR);
  const [kind, setKind] = createSignal<ScheduleKind>("daily");
  const [weekdays, setWeekdays] = createSignal<number[]>([1, 2, 3, 4, 5]);
  const [timesPerWeek, setTimesPerWeek] = createSignal(DEFAULT_TIMES_PER_WEEK);
  const [goalKind, setGoalKind] = createSignal<GoalKind>("binary");
  const [targetAmount, setTargetAmount] = createSignal(DEFAULT_TARGET_AMOUNT);
  const [targetUnit, setTargetUnit] = createSignal("");
  const [reminderTime, setReminderTime] = createSignal("");
  const [localConfirmDeleteOpen, setLocalConfirmDeleteOpen] = createSignal(false);

  // Delete confirmation: controlled via URL if parent manages it (#15b),
  // otherwise local signal. `setConfirmDeleteOpen` routes to the correct channel.
  const confirmDeleteOpen = () => props.confirmDeleteOpen ?? localConfirmDeleteOpen();
  const setConfirmDeleteOpen = (open: boolean) => {
    if (props.onConfirmDeleteOpenChange) {
      props.onConfirmDeleteOpenChange(open);
    } else {
      setLocalConfirmDeleteOpen(open);
    }
  };

  const isEditing = () => Boolean(props.habit);

  // Synchronizes form each time it opens.
  createEffect(() => {
    if (!props.open) {
      return;
    }
    const habit = props.habit ?? null;
    if (habit) {
      setName(habit.name ?? "");
      setIcon(habit.icon ?? DEFAULT_HABIT_ICON);
      setColor(habit.color ?? DEFAULT_HABIT_COLOR);
      setKind(
        habit.schedule_kind === "weekdays" || habit.schedule_kind === "times_per_week"
          ? habit.schedule_kind
          : "daily",
      );
      const parsed = parseWeekdays(habit.weekdays);
      setWeekdays(parsed.length > 0 ? parsed : [1, 2, 3, 4, 5]);
      setTimesPerWeek(habit.times_per_week ?? DEFAULT_TIMES_PER_WEEK);
      setGoalKind(habit.goal_kind === "quantified" ? "quantified" : "binary");
      setTargetAmount(habit.target_amount ?? DEFAULT_TARGET_AMOUNT);
      setTargetUnit(habit.target_unit ?? "");
      setReminderTime(props.reminders?.[0]?.time_of_day ?? "");
    } else {
      setName("");
      setIcon(DEFAULT_HABIT_ICON);
      setColor(DEFAULT_HABIT_COLOR);
      setKind("daily");
      setWeekdays([1, 2, 3, 4, 5]);
      setTimesPerWeek(DEFAULT_TIMES_PER_WEEK);
      setGoalKind("binary");
      setTargetAmount(DEFAULT_TARGET_AMOUNT);
      setTargetUnit("");
      setReminderTime("");
    }
  });

  const weekdayLabels = () => [
    t(msg`Mon`),
    t(msg`Tue`),
    t(msg`Wed`),
    t(msg`Thu`),
    t(msg`Fri`),
    t(msg`Sat`),
    t(msg`Sun`),
  ];

  const scheduleOptions = () => [
    { value: "daily" as const, label: t(msg`Daily`) },
    { value: "weekdays" as const, label: t(msg`Days of week`) },
    { value: "times_per_week" as const, label: t(msg`Per week`) },
  ];

  const toggleWeekday = (day: number) => {
    setWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].toSorted((a, b) => a - b),
    );
  };

  const canSave = () => {
    // Local DB must be ready: mutations throw if drizzle() is null.
    if (!drizzle()) {
      return false;
    }
    if (name().trim().length === 0) {
      return false;
    }
    if (kind() === "weekdays" && weekdays().length === 0) {
      return false;
    }
    if (goalKind() === "quantified" && !(targetAmount() >= 1)) {
      return false;
    }
    return !mutations.isSubmitting();
  };

  const syncReminder = async (habitId: string) => {
    const existing = props.reminders?.[0];
    const time = reminderTime();
    if (time) {
      await mutations.upsertReminder(habitId, time, existing ? { id: existing.id } : undefined);
    } else if (existing) {
      await mutations.deleteReminder(existing.id);
    }
  };

  const handleSave = async () => {
    if (!canSave()) {
      return;
    }
    const habit = props.habit ?? null;
    const quantified = goalKind() === "quantified";
    if (habit) {
      await mutations.updateHabit(habit.id, {
        name: name(),
        icon: icon(),
        color: color(),
        schedule_kind: kind(),
        weekdays: kind() === "weekdays" ? serializeWeekdays(weekdays()) : null,
        times_per_week: kind() === "times_per_week" ? timesPerWeek() : null,
        goal_kind: goalKind(),
        target_amount: quantified ? Math.max(1, targetAmount()) : null,
        target_unit: quantified ? normalizeUnit(targetUnit()) : null,
      });
      await syncReminder(habit.id);
    } else {
      const id = await mutations.createHabit({
        name: name(),
        icon: icon(),
        color: color(),
        scheduleKind: kind(),
        weekdays: kind() === "weekdays" ? weekdays() : undefined,
        timesPerWeek: kind() === "times_per_week" ? timesPerWeek() : undefined,
        sortOrder: props.nextSortOrder ?? 0,
        goalKind: goalKind(),
        targetAmount: quantified ? Math.max(1, targetAmount()) : null,
        targetUnit: quantified ? normalizeUnit(targetUnit()) : null,
      });
      if (id && reminderTime()) {
        await mutations.upsertReminder(id, reminderTime());
      }
    }
    props.onOpenChange(false);
  };

  const handleArchiveToggle = async () => {
    const habit = props.habit ?? null;
    if (!habit) {
      return;
    }
    await mutations.archiveHabit(habit.id, habit.archived !== 1);
    props.onOpenChange(false);
  };

  const handleDelete = async () => {
    const habit = props.habit ?? null;
    if (!habit) {
      return;
    }
    await mutations.deleteHabit(habit.id);
    setConfirmDeleteOpen(false);
    props.onOpenChange(false);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogHeader>
        <DialogTitle>{isEditing() ? t(msg`Edit habit`) : t(msg`New habit`)}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-4 p-4 pt-2 overflow-y-auto">
        {/* Nombre */}
        <div class="flex flex-col gap-1.5">
          <Label for="habit-name">{t(msg`Name`)}</Label>
          <TextField
            id="habit-name"
            data-testid="habit-name-input"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder={t(msg`e.g. Read 20 minutes`)}
            maxLength={80}
          />
        </div>

        {/* Icono */}
        <div class="flex flex-col gap-1.5">
          <Label>{t(msg`Icon`)}</Label>
          <div class="grid grid-cols-8 gap-1">
            <For each={[...HABIT_ICONS]}>
              {(emoji) => (
                <button
                  type="button"
                  onClick={() => setIcon(emoji)}
                  class={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md text-lg transition-colors",
                    icon() === emoji ? "bg-primary/20 ring-2 ring-primary" : "hover:bg-accent/10",
                  )}
                  aria-label={emoji}
                >
                  {emoji}
                </button>
              )}
            </For>
          </div>
        </div>

        {/* Color */}
        <div class="flex flex-col gap-1.5">
          <Label>{t(msg`Color`)}</Label>
          <div class="flex flex-wrap gap-2">
            <For each={[...HABIT_COLORS]}>
              {(hex) => (
                <button
                  type="button"
                  onClick={() => setColor(hex)}
                  class={cn(
                    "h-7 w-7 rounded-full transition-transform",
                    color() === hex ? "ring-2 ring-offset-2 ring-ring scale-110" : "",
                  )}
                  style={{ "background-color": hex }}
                  aria-label={hex}
                />
              )}
            </For>
          </div>
        </div>

        {/* Schedule */}
        <div class="flex flex-col gap-1.5">
          <Label>{t(msg`Schedule`)}</Label>
          <Select
            data-testid="habit-schedule-kind"
            aria-label={t(msg`Schedule`)}
            options={scheduleOptions()}
            value={kind()}
            onChange={(value) => {
              if (value) {
                setKind(value);
              }
            }}
          />

          <Show when={kind() === "weekdays"}>
            <div class="flex gap-1 pt-1">
              <For each={[...WEEKDAY_KEYS]}>
                {(day) => (
                  <button
                    type="button"
                    onClick={() => toggleWeekday(day)}
                    class={cn(
                      "flex h-8 flex-1 items-center justify-center rounded-md text-xs font-medium transition-colors",
                      weekdays().includes(day)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/40 text-muted-foreground hover:bg-accent/10",
                    )}
                  >
                    {weekdayLabels()[day - 1]}
                  </button>
                )}
              </For>
            </div>
          </Show>

          <Show when={kind() === "times_per_week"}>
            <div class="flex items-center gap-3 pt-1">
              <input
                type="range"
                min="1"
                max={MAX_TIMES_PER_WEEK}
                value={timesPerWeek()}
                onInput={(e) => setTimesPerWeek(Number(e.currentTarget.value))}
                class="flex-1 accent-[var(--primary)]"
              />
              <span class="w-24 text-sm text-muted-foreground">
                {t(msg`{count} × week`, { count: timesPerWeek() })}
              </span>
            </div>
          </Show>
        </div>

        {/* Meta: binaria vs cuantificable */}
        <div class="flex flex-col gap-1.5">
          <Label>{t(msg`Goal`)}</Label>
          <div
            class="grid grid-cols-2 gap-1 rounded-md bg-muted/40 p-1"
            data-testid="habit-goal-kind"
          >
            <button
              type="button"
              data-testid="goal-binary"
              onClick={() => setGoalKind("binary")}
              class={cn(
                "rounded px-2 py-1.5 text-xs font-medium transition-colors",
                goalKind() === "binary"
                  ? "bg-card shadow text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {t(msg`Yes / No`)}
            </button>
            <button
              type="button"
              data-testid="goal-quantified"
              onClick={() => setGoalKind("quantified")}
              class={cn(
                "rounded px-2 py-1.5 text-xs font-medium transition-colors",
                goalKind() === "quantified"
                  ? "bg-card shadow text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {t(msg`Measurable`)}
            </button>
          </div>

          <Show when={goalKind() === "quantified"}>
            <div class="flex items-end gap-2 pt-1">
              <div class="flex w-28 flex-col gap-1">
                <Label for="habit-target-amount">{t(msg`Daily target`)}</Label>
                <TextField
                  id="habit-target-amount"
                  data-testid="habit-target-amount"
                  type="number"
                  min={1}
                  max={MAX_TARGET_AMOUNT}
                  value={String(targetAmount())}
                  onInput={(e) => {
                    const n = Number(e.currentTarget.value);
                    setTargetAmount(Number.isFinite(n) ? Math.trunc(n) : 0);
                  }}
                />
              </div>
              <div class="flex flex-1 flex-col gap-1">
                <Label for="habit-target-unit">{t(msg`Unit`)}</Label>
                <TextField
                  id="habit-target-unit"
                  data-testid="habit-target-unit"
                  value={targetUnit()}
                  onInput={(e) => setTargetUnit(e.currentTarget.value)}
                  placeholder={t(msg`e.g. glasses, min, pages`)}
                  maxLength={24}
                />
              </div>
            </div>
          </Show>
        </div>

        {/* Recordatorio */}
        <div class="flex flex-col gap-1.5">
          <Label for="habit-reminder">{t(msg`Reminder (time of day)`)}</Label>
          <div class="flex items-center gap-2">
            <input
              id="habit-reminder"
              type="time"
              value={reminderTime()}
              onInput={(e) => setReminderTime(e.currentTarget.value)}
              class="h-10 rounded-md border border-input bg-background px-3 text-sm"
            />
            <Show when={reminderTime()}>
              <Button variant="ghost" size="sm" onClick={() => setReminderTime("")}>
                {t(msg`Clear`)}
              </Button>
            </Show>
          </div>
        </div>

        {/* Acciones */}
        <div class="flex items-center justify-between gap-2 pt-2">
          <Show when={isEditing()} fallback={<div />}>
            <div class="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void handleArchiveToggle()}
                disabled={!drizzle() || mutations.isSubmitting()}
                data-testid="habit-archive"
              >
                <Show
                  when={props.habit?.archived === 1}
                  fallback={
                    <>
                      <Archive class="mr-1 h-4 w-4" />
                      {t(msg`Archive`)}
                    </>
                  }
                >
                  <ArchiveRestore class="mr-1 h-4 w-4" />
                  {t(msg`Unarchive`)}
                </Show>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="text-destructive hover:text-destructive"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={!drizzle() || mutations.isSubmitting()}
                data-testid="habit-delete"
              >
                <Trash2 class="mr-1 h-4 w-4" />
                {t(msg`Delete`)}
              </Button>
            </div>
          </Show>

          <div class="flex items-center gap-2">
            <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!canSave()}
              data-testid="habit-save"
            >
              {isEditing() ? t(msg`Save changes`) : t(msg`Create habit`)}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen()}
        onOpenChange={setConfirmDeleteOpen}
        title={t(msg`Delete habit?`)}
        description={t(
          msg`The habit moves to the trash with its whole history. You can restore it anytime from Profile → Trash.`,
        )}
        confirmLabel={t(msg`Delete habit`)}
        variant="destructive"
        onConfirm={() => void handleDelete()}
      />
    </Dialog>
  );
}
